<template>
  <div>
    <div v-if="$slots.label" class="vcl-date-picker__label-row">
      <label ref="label" class="vcl-date-picker__label">
        <slot name="label" />
      </label>
    </div>
    <div ref="date-picker-container" class="vcl-date-picker" :style="{ width, height }" :class="{ 'custom': timeType === 'custom', 'is-clickable': timeType === 'custom' || timeType === 'day' || isPickableFixedRange, disabled }" tabindex="0" @click="(timeType === 'custom' || timeType === 'day' || isPickableFixedRange) && toggle($event)" @keyup="containerKeyup($event)" @keypress.space.prevent @keydown.left.right.prevent>
      <date-picker ref="date-picker" v-model="datePickerValue" :locale="locale" mode="date" :max-date="maxDate" :is-range="isRange">
        <template #default="{ inputValue }">
          <vp-icon v-if="timeType !== 'custom'" class="vcl-date-picker__arrow arrow--left" icon="chevron-left" tabindex="0" role="button" aria-label="Previous Day" @click.stop="modifyOffset(-1)" @keyup.stop.prevent="chevronKeyup($event,-1)" @keypress.space.prevent />
          <p class="vcl-date-picker__display" @click.stop="(timeType === 'custom' || timeType === 'day' || disabled || isPickableFixedRange) && toggleToPage()">
          {{ formatDate(inputValue) }}
          </p>
          <vp-icon class="vcl-date-picker__calendar-icon" icon="calendar" />
          <vp-icon v-if="timeType !== 'custom'" class="vcl-date-picker__arrow arrow--right" :class="{'is-disabled': isNextDisabled }" tabindex="0" role="button" icon="chevron-right" aria-label="Next Day" @click.stop.prevent="!isNextDisabled && modifyOffset(1)" @keypress.space.prevent />
        </template>
      </date-picker>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import DatePicker from '@virtual-peaker/v-calendar/src/components/DatePicker.vue';
import VpIcon from '../../vp-icon/VpIcon.vue';

export default {
  name: 'VpChartDatePicker',
  components: {
    VpIcon,
    DatePicker,
  },
  props: {
    value: {
      type: Object,
      default() {
        return {
          start: new Date(),
          end: new Date(),
        };
      },
    },
    type: {
      type: String,
      default: 'day',
    },
    width: {
      type: String,
      default: '100%',
    },
    height: {
      type: String,
      default: '40px',
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    placeholder: {
      type: String,
      default: null,
    },
    locale: {
      type: String,
      default: 'en',
    },
    maxDate: {
      type: Date,
      default: () => new Date(),
    },
    enableFixedRangePicker: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update'],
  data() {
    return {
      timeType: 'day',
      timeOffset: 0,
      startTime: moment(Date.now()),
      endTime: moment(Date.now()),
      dateValue: moment(Date.now()),
      format: 'MM/DD/YYYY',
    };
  },
  computed: {
    isNextDisabled() {
      if (!this.maxDate) return false;

      const { end } = this.getTimes(this.timeType, this.timeOffset, this.startTime, this.endTime);
      const maxDate = moment(this.maxDate).startOf('day');
      const endDate = moment(end).startOf('day');

      return maxDate.diff(endDate, 'days') <= 0;
    },
    isRange() {
      return (this.timeType !== 'day' && !this.enableFixedRangePicker);
    },
    isPickableFixedRange(){
      return ((this.timeType === 'month' || this.timeType === 'week') && this.enableFixedRangePicker);
    },
    datePickerValue: {
      get() {
        return (this.isRange || this.isPickableFixedRange) ? { start: this.startTime, end: this.endTime } : this.startTime;
      },
      set(e) {
        if (e) {
          this.startTime = this.isRange ? e.start : e;
          this.endTime = this.isRange ? e.end : null;
        }
        if (this.isPickableFixedRange) {
          const { start, end } = this.calculateStartEndDates(this.timeType);
          this.startTime = start;
          this.endTime = end;
        }
        this.update(false, true);
      },
    },
  },
  watch: {
    value(value, prev) {
      const { start: prevStart = new Date(), end: prevEnd = new Date() } = prev;
      const { start = new Date(), end = new Date() } = value;
      // if the value has not changed, don't do the updates
      if (start.getTime() === prevStart.getTime() && end.getTime() === prevEnd.getTime()) {
        return null;
      }
      this.startTime = moment(start);
      this.endTime = moment(end);
      this.update();
    },
    type: {
      immediate: true,
      handler(value) {
        this.timeType = value;
        this.modifyType();
        this.update(true);
      },
    },
    locale: {
      immediate: true,
      handler(value) {
        moment.locale(value);
        this.format = value === 'es' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
        this.update(true);
      },
    }
  },
  methods: {
    toggleToPage() {
      this.$refs['date-picker'].togglePopover();
      this.$nextTick(() => this.$refs['date-picker'].move(this.startTime.toDate(), { transition: 'none' }));
    },
    toggle(e) {
      e.stopPropagation();
      if (!this.disabled) {
        this.toggleToPage()
      }
    },
    toISO(value) {
      if (typeof value === 'object') {
        const { start, end } = value;
        return { start: start.toISOString(), end: end.toISOString() };
      }
      return value.toISOString();
    },
    chevronKeyup(event, direction) {
      if ((event.code === 'Space') || (event.code === 'Enter')) {
        this.modifyOffset(direction);
      }
    },
    containerKeyup(event) {
      event.preventDefault();
      event.stopPropagation();

      const { code } = event;
      switch (code) {
        case 'Space':
        case 'Enter':
          this.toggle(event);
          break;
        case 'ArrowLeft':
          this.modifyOffset(-1);
          break;
        case 'ArrowRight':
          if (!this.isNextDisabled) {
            this.modifyOffset(1);
          }
          break;
        default:
          break;
      }
    },
    formatDate(value) {
      let date = value;
      if (!date || !date.start) {
        date = (this.isRange || this.isPickableFixedRange) ? { start: this.startTime.toDate(), end: this.endTime.toDate() } : this.startTime;
      }

      if (this.isRange || this.isPickableFixedRange) {
        const { start, end } = date;
        const startTime = moment(start, this.format);
        const endTime = moment(end, this.format);

        if (startTime.isSame(end, 'year') && startTime.isSame(end, 'month')) {
          if (this.locale === 'es') {
            return `${startTime.format('D')}-${endTime.format('D MMM YYYY')}`;
          }
          return `${startTime.format('MMM D')}-${endTime.format('D, YYYY')}`;
        }
        if (this.timeType === 'year') {
          if (this.locale === 'es') {
            return `${startTime.format('D MMM')} - ${endTime.format('D MMM, YYYY')}`;
        }
          return `${startTime.format('MMM D')} - ${endTime.format('MMM D, YYYY')}`;
        }
        if (this.locale === 'es') {
          return `${startTime.format('D MMM')} - ${endTime.format('D MMM')}`;
        }
        return `${startTime.format('MMM D')} - ${endTime.format('MMM D')}`;
      }

      return this.locale === 'es' ? moment(date).format('D MMM YYYY') : moment(date).format('MMM D, YYYY');
    },
    getTimes(timeType, timeOffset, startTime, endTime) {
      let start;
      let end;
      switch (timeType) {
        case 'week':
          end = moment().add(timeOffset, 'week').endOf('week');
          start = moment().add(timeOffset, 'week').startOf('week');
          break;
        case 'month':
          end = moment().add(timeOffset, 'month').endOf('month');
          start = moment().add(timeOffset, 'month').startOf('month');
          break;
        case 'year':
          end = moment().add(timeOffset, 'year').endOf('year');
          start = moment().add(timeOffset, 'year').startOf('year');
          break;
        case 'custom':
          end = moment(endTime);
          start = moment(startTime);
          break;
        case 'day':
        default:
          end = moment(startTime).add(timeOffset, 'day').endOf('day');
          start = moment(startTime).add(timeOffset, 'day').startOf('day');
          break;
      }

      return {
        start: start.toDate(),
        end: end.toDate(),
      };
    },
    modifyType() {
      if (this.disabled) {
        return null;
      }
      this.busy = true;
      this.timeOffset = 0;
      this.update(true);
    },
    modifyOffset(delta) {
      if (this.disabled) {
        return null;
      }
      if (this.timeType !== 'custom') {
        if (this.timeType === 'day') {
          if (delta === -1 || this.startTime !== moment()) {
            this.timeOffset = delta;
            this.busy = true;
            this.update();
          }
          this.timeOffset = 0;
        } else {
          this.timeOffset = this.timeOffset + delta <= 0 ? this.timeOffset + delta : 0;
          this.busy = true;
          this.update();
        }
      }
    },
    calculateStartEndDates(timeType){
          const from_date = moment(this.startTime).startOf(timeType);
          const to_date = moment(this.startTime).endOf(timeType);
          return {start: from_date.toDate(), end: to_date.toDate()}
    },
    update(force = false, handPicked = false) {
      if (force) {
        this.startTime = moment();
        this.endTime = moment();
      }
      const currentTimeType = this.timeType;
      //Must set new timeOffset after manually picking a week or month so chevrons will work as expected
      if (handPicked && this.isPickableFixedRange) {
        if (['week', 'month'].includes(this.timeType)) this.timeOffset = (moment().diff(this.startTime, this.timeType)) * -1;
        this.timeType = 'custom';
      }

      const { start, end } = this.getTimes(this.timeType, this.timeOffset, this.startTime, this.endTime);
      this.timeType = currentTimeType;
      this.startTime = moment(start);
      this.endTime = moment(end);

      this.$emit('update', { start, end });
      this.$emit('input', { start, end });
      this.$nextTick(() => {
        if (this.$refs['date-picker']) {
          this.$refs['date-picker'].value_ = { ...(this.$refs['date-picker']?.value_ || {}), start, end };
          if (this.$refs['date-picker']?.$refs?.calendar) {
            this.$refs['date-picker'].move(start, { transition: 'none' });
          }
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
  .vcl-date-picker {
    $self: &;
    box-sizing: border-box;
    user-select: none;
    background: #FFFFFF;
    border: 1px solid #AFAFAF;
    border-radius: 6px;
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 0;
    padding: 0 49px 0 41px;
    width: 100%;
    height: 100%;
    &:focus {
      border: 3px solid #016999;
      outline: none;
    }
    &.is-clickable {
      cursor: pointer;
    }

    span {
      color: #4F4F4F;
      font-size: 16px;
      height: 100%;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      user-select: none;
      padding: 0;
      margin: 0;
    }

    &__display {
      margin: 0;
      padding: 0;
    }

    &__arrow {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      z-index: 1;
      margin: 0;
      color: #016699;
      font-size: 13.5px;
      cursor: pointer;
      &:focus {
        border: none;
        outline: none;
      }
      &.arrow {
        &--left {
          margin-top: 1px;
          left: 14px;
        }
        &--right {
          margin-bottom: 1px;
          right: 14px;
          &.is-disabled {
            color: #D8D8D8;
          }
        }
      }
    }

    &__calendar-icon {
      font-size: 16px;
      color: #016699;
      margin: 1.5px 0 0;
      padding: 0;
    }
    &.custom {
      padding: 0 16px;
    }
    &.disabled {
      color: #AFAFAF;
      background-color: #F8F8F8;
      cursor: not-allowed;
      border: 1px solid #AFAFAF;
      * {
        cursor: not-allowed;
      }
      #{ $self }__calendar-icon {
        color: #AFAFAF;
        .icon {
          color: #AFAFAF;
        }
      }
      #{ $self }__arrow {
        color: #AFAFAF;
        .icon {
          color: #AFAFAF;
        }
      }
      #{ $self }__picker {
        span {
          color: #AFAFAF;
        }
        .icon {
          color: #AFAFAF;
        }
      }
    }
  }
</style>
